import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { eq, gt } from 'ember-truth-helpers';

import PrismIcon from './icon.gts';

const PAGINATION_BREAK_CONSTANT = 10; // The amount of total pages on which the pagination will break into pieces with '...'
const LEFT_LIMIT = 8; // Page number from when to start going into middle-mode [1 ... 6 7 8 9 10 11 ... 100]
const RIGHT_LIMIT = 8; // Page number from when to start going into trail-mode [1 ... 93 94 95 96 97 98 99 100]
const TURBO_PAGE_SKIP_VALUE = 10;

// In middle-mode, how many pages to the left/right of the current page
const MIDDLE_LEFT_PAGES = 3;
const MIDDLE_RIGHT_PAGES = 3;

interface PrismPaginationSignature {
  Args: {
    lastPage: number;
    page: number;
    setPage: (page: number) => unknown;
  };
  Element: HTMLDivElement;
}

export default class PrismPagination extends Component<PrismPaginationSignature> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get nextPageDisabled() {
    return this.page === this.args.lastPage;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get page() {
    return this.args.page ?? 1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get pages() {
    // eslint-disable-next-line @typescript-eslint/unbound-method
    const { _setPage, turboPreviousPage, turboNextPage } = this;
    const { lastPage } = this.args;
    const pages = [];

    // More than 10 pages? Special attention to page breaking with '...'
    if (lastPage > PAGINATION_BREAK_CONSTANT) {
      if (lastPage - this.page < RIGHT_LIMIT) {
        // When nearing the end of the pagination...
        pages.push(
          ...[
            { value: 1, onClick: _setPage },
            { value: '...' as const, onClick: turboPreviousPage },
          ],
        );

        for (let i = lastPage - RIGHT_LIMIT; i <= lastPage; i++) {
          pages.push({ value: i, onClick: _setPage });
        }
      } else if (this.page < LEFT_LIMIT) {
        // When at the beginning of the pagination...
        for (let i = 1; i <= LEFT_LIMIT + 1; i++) {
          pages.push({ value: i, onClick: _setPage });
        }

        pages.push(
          ...[
            { value: '...' as const, onClick: turboNextPage },
            { value: lastPage, onClick: _setPage },
          ],
        );
      } else {
        // When somewhere in the middle...
        pages.push(
          ...[
            { value: 1, onClick: _setPage },
            { value: '...' as const, onClick: turboPreviousPage },
          ],
        );

        for (
          let i = this.page - MIDDLE_LEFT_PAGES;
          i <= this.page + MIDDLE_RIGHT_PAGES;
          i++
        ) {
          pages.push({ value: i, onClick: _setPage });
        }

        pages.push(
          ...[
            { value: '...' as const, onClick: turboNextPage },
            { value: lastPage, onClick: _setPage },
          ],
        );
      }
    } else {
      // Less than 10 pages? Just show them all inline
      for (let i = 1; i <= lastPage; i++) {
        pages.push({ value: i, onClick: _setPage });
      }
    }

    return pages;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get prevPageDisabled() {
    return this.page === 1;
  }

  @action
  _setPage(page: number | '...'): void {
    if (page !== '...') {
      this.args.setPage(page);
    }
  }

  @action
  nextPage(): void {
    if (!this.nextPageDisabled) {
      this.args.setPage(this.page + 1);
    }
  }

  @action
  prevPage(): void {
    if (!this.prevPageDisabled) {
      this.args.setPage(this.page - 1);
    }
  }

  @action
  turboNextPage(): void {
    if (this.page + TURBO_PAGE_SKIP_VALUE < this.args.lastPage) {
      this.args.setPage(this.page + TURBO_PAGE_SKIP_VALUE);
    } else {
      this.args.setPage(this.args.lastPage);
    }
  }

  @action
  turboPreviousPage(): void {
    if (this.page - TURBO_PAGE_SKIP_VALUE > 1) {
      this.args.setPage(this.page - TURBO_PAGE_SKIP_VALUE);
    } else {
      this.args.setPage(1);
    }
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    {{#if (gt this.pages.length 0)}}
      <div
        aria-label="Pagination Navigation"
        class="prism-pagination-container"
        ...attributes
      >
        <button
          aria-disabled={{this.prevPageDisabled}}
          aria-label="Previous Page"
          class="pagination-button pagination-previous-button
            {{if this.prevPageDisabled 'disabled'}}"
          data-test-id="pagination-prev-btn"
          type="button"
          {{on "click" this.prevPage}}
        >
          <PrismIcon @name="chevron-left" @size="extra-small" />
        </button>
        {{#each this.pages as |currentPage|}}
          <button
            aria-current={{if (eq currentPage.value this.page) "page"}}
            aria-label="Page {{currentPage.value}}"
            class="pagination-button
              {{if (eq currentPage.value this.page) 'current-page'}}"
            data-test-id="pagination-page-{{currentPage.value}}"
            type="button"
            {{on "click" (fn currentPage.onClick currentPage.value)}}
          >
            <span>
              {{currentPage.value}}
            </span>
          </button>
        {{/each}}
        <button
          aria-disabled={{this.nextPageDisabled}}
          aria-label="Next Page"
          class="pagination-button pagination-next-button
            {{if this.nextPageDisabled 'disabled'}}"
          data-test-id="pagination-next-btn"
          type="button"
          {{on "click" this.nextPage}}
        >
          <PrismIcon @name="chevron-right" @size="extra-small" />
        </button>
      </div>
    {{/if}}
  </template>
}
